import { BrandMark } from '../../branding/godgift/BrandMark';
import { BRAND } from '../lib/brand';
import { CATEGORY_META, CATEGORY_ORDER, iconForCategory } from '../lib/categoryGrid';
import { Icon } from './Icon';

/**
 * S0 Attract / welcome resting scene (R15 T1 — the pre-connect resting state).
 *
 * Field context (user's pre-connect screenshot review): the resting showcase used to show the
 * IN-CALL S1 contact form ("who do I have the pleasure…") and a Browse path that dead-ended
 * ("connect to proceed"). That read as an AI-generic form-first screen, not a premium fair booth.
 * S0 replaces it with a true attract/welcome scene:
 *
 *   - a brand welcome (house name + a two-line blurb + the "2,000+ pieces / 13 categories" stat),
 *   - a soft ambient brass wash treated quietly (design-language §8 — hairlines + space, no chrome),
 *     with a R29 X1 slow warm sheen-sweep pass over it (a gallery "softly lit" feel, no stock photos),
 *   - ONE cue: "Tap Start talking — Gigi will show you around." pointing at the connect control.
 *
 * NO form, NO category grid, NO Browse/Just-browsing affordance pre-connect (T2). A tap anywhere on
 * the ambient stage just pulses the cue (there's nothing to advance to — Start talking is the only
 * door). The contact card becomes the first IN-CALL beat (scene 'welcome', reached on connect).
 *
 * All copy comes through the overlay `strings.json` (BRAND.*), NOT hardcoded. The house blurb + the
 * "2,000+ pieces / 13 categories" stat are the CLIENT-APPROVED positioning set (landed 2026-07-16,
 * 971c23c — "final user positioning", R46 §Needs-Andy decisions 1–3 resolved; logged in the
 * orchestration-log). A sensible fallback renders if a string is missing so the scene never blanks.
 *
 * Presentation only, tokens only, day + night, reduced-motion safe (the ambient wash holds still
 * under prefers-reduced-motion via index.css). No emoji (line icons only, §4).
 *
 * NOTE (T2): session-less BROWSE (letting a visitor explore the range without connecting) returns
 * with the R1 degraded-mode build — one build, two jobs. Until then the resting state is attract-only.
 */

type Props = {
  /** Product/session identifier, for a quiet reference line (optional; hidden when absent). */
  conversationId?: string;
};

const FALLBACK_TITLE = 'Welcome';
const FALLBACK_LINES = [
  'A home-decor house, showing our range at the fair.',
  'Our concierge will walk you through the collection and prepare a spec sheet of the pieces you love.',
];
const FALLBACK_CUE = 'Tap Start talking — the concierge will show you around.';

export function AttractScene({ conversationId }: Props) {
  const title = typeof BRAND.attract_title === 'string' ? BRAND.attract_title : FALLBACK_TITLE;
  const lines = Array.isArray(BRAND.attract_lines) ? BRAND.attract_lines : FALLBACK_LINES;
  const stat = typeof BRAND.attract_stat === 'string' ? BRAND.attract_stat : undefined;
  const cue = typeof BRAND.attract_cue === 'string' ? BRAND.attract_cue : FALLBACK_CUE;

  return (
    <div
      className="attract-ambient reveal relative flex min-h-[60vh] flex-col justify-center gap-8 overflow-hidden rounded-lg p-8 sm:p-10"
      data-testid="attract-scene"
    >
      {/* R29 X1 — a slow warm SHEEN SWEEP over the ambient wash: a wide, very low-opacity brass band
          drifts diagonally across the resting stage every ~26s, so the idle scene feels "softly lit"
          (a gallery under gentle wash) rather than static. transform-only (compositor-friendly), sits
          BEHIND the content, decorative (aria-hidden). Reduced-motion → the sweep holds still (index.css
          zeroes the animation). NO stock photos — pure token-driven light. */}
      <span aria-hidden className="attract-sheen pointer-events-none absolute inset-0 z-0" />

      {/* Brand welcome — editorial, foil-gradient house name (the gallery entrance). R28 (3.3): the
          generic sparkle badge is replaced by the Godgift BrandMark (a serif-"G" monogram in brushed
          brass — a real branding asset, swapped for the client's vector logo at P4.3 by a single-file
          drop in branding/godgift/BrandMark.tsx). */}
      <div className="relative z-10 flex flex-col gap-4">
        <BrandMark size={56} title={title} />
        <h2 className="font-display text-3xl leading-tight text-foil sm:text-4xl">{title}</h2>
        <div className="flex flex-col gap-2 max-w-md">
          {lines.map((line) => (
            <p key={line} className="text-sm leading-relaxed text-subtle sm:text-base">
              {line}
            </p>
          ))}
        </div>
        {/* R28 (3.3): the range stat tidied into a proper brass badge (soft brass-tint fill + hairline
            ring), not a bare icon+caption line. Copy unchanged (client-approved positioning). */}
        {stat && (
          <span className="mt-1 inline-flex w-fit items-center gap-2 rounded-full bg-brass-soft px-3.5 py-1.5 text-xs uppercase tracking-[0.14em] text-brass ring-1 ring-brass/30">
            <Icon name="grid" size={13} />
            {stat}
          </span>
        )}
      </div>

      {/* The single cue — points at the Start talking control in the voice panel. */}
      <div
        className="relative z-10 inline-flex w-fit items-center gap-2.5 rounded-full bg-fill px-5 py-3 text-sm text-ink ring-1 ring-line"
        data-testid="attract-cue"
      >
        <span className="attract-cue-pulse grid h-7 w-7 place-items-center rounded-full bg-brass text-on-accent">
          <Icon name="play" size={14} />
        </span>
        {cue}
      </div>

      {/* R28 (3.4) — the pre-connect DEAD-ZONE fill: a quiet CATEGORIES TEASER strip (design call in
          the R28 review log). It previews the breadth the visitor is about to explore, reusing the
          CATEGORY_META vocabulary (no new data / no wiring). DISPLAY-ONLY pre-connect — the real,
          tappable server-driven grid (CategoryGrid) is the in-call browse surface; these chips just
          say "here's the range". Chosen over an ambient photo-drift carousel because we have no wired,
          licence-cleared still set here and the scene already owns the brass ambient wash — a photo
          carousel would compete with it, whereas the teaser sells the range and stays token-pure. */}
      <CategoryTeaser />

      {conversationId && (
        <p className="relative z-10 text-[10px] text-faint" data-testid="attract-ref">
          ref {conversationId.slice(0, 8)}
        </p>
      )}
    </div>
  );
}

/**
 * R28 (3.4) — the pre-connect categories teaser strip. DISPLAY-ONLY (no tap handler pre-connect —
 * the tappable browse grid is the in-call CategoryGrid). Quiet brass-glyph chips, hairline framing,
 * wrapping across kiosk breakpoints. Tokens only; reduced-motion safe (no motion of its own — it
 * rides the parent scene's `.reveal`). Aria-hidden as a whole: it's a decorative range preview, and
 * the house blurb above already tells assistive tech what this booth is.
 */
function CategoryTeaser() {
  return (
    <div
      className="relative z-10 flex flex-col gap-2.5"
      aria-hidden="true"
      data-testid="attract-categories"
    >
      <span className="text-[11px] uppercase tracking-[0.18em] text-faint">Explore the range</span>
      <div className="flex flex-wrap gap-2">
        {CATEGORY_ORDER.map((slug) => (
          <span
            key={slug}
            className="inline-flex items-center gap-1.5 rounded-full bg-fill px-3 py-1.5 text-xs text-subtle ring-1 ring-line"
          >
            <Icon name={iconForCategory(slug)} size={13} className="shrink-0 text-brass/80" />
            {CATEGORY_META[slug]?.name ?? slug}
          </span>
        ))}
      </div>
    </div>
  );
}
